<!--
  - SPDX-FileCopyrightText: 2023 Nextcloud GmbH and Nextcloud contributors
  - SPDX-License-Identifier: AGPL-3.0-or-later
-->

<template>
	<NcDialog
		class="dialog"
		size="small"
		:name="t('settings', 'New account')"
		out-transition
		v-on="$listeners">
		<form
			id="new-user-form"
			class="dialog__form"
			data-test="form"
			:disabled="loading.all"
			@submit.prevent="createUser">
			<UserFormFields
				ref="fields"
				:field-config="fieldConfig"
				:quota-options="quotaOptions" />
		</form>

		<template #actions>
			<NcButton
				class="dialog__submit"
				data-test="submit"
				form="new-user-form"
				variant="primary"
				type="submit">
				{{ t('settings', 'Add new account') }}
			</NcButton>
		</template>
	</NcDialog>
</template>

<script>
import NcButton from '@nextcloud/vue/components/NcButton'
import NcDialog from '@nextcloud/vue/components/NcDialog'
import UserFormFields from './UserFormFields.vue'

export default {
	name: 'NewUserDialog',

	components: {
		NcButton,
		NcDialog,
		UserFormFields,
	},

	// Children inject this reactive object and mutate its properties via v-model.
	// Do not reassign newUser entirely, the injected reference would go stale.
	provide() {
		return {
			formData: this.newUser,
		}
	},

	props: {
		loading: {
			type: Object,
			required: true,
		},

		newUser: {
			type: Object,
			required: true,
		},

		quotaOptions: {
			type: Array,
			required: true,
		},
	},

	emits: ['closing'],

	computed: {
		settings() {
			return this.$store.getters.getServerData
		},

		usernameLabel() {
			if (this.settings.newUserGenerateUserID) {
				return t('settings', 'Account name will be autogenerated')
			}
			return t('settings', 'Account name (required)')
		},

		/**
		 * Reactive field configuration passed to UserFormFields.
		 * Controls visibility, labels, and required state for each field
		 * based on the current form values and server settings.
		 */
		fieldConfig() {
			return {
				username: {
					show: true,
					label: this.usernameLabel,
					disabled: this.settings.newUserGenerateUserID,
					required: true,
				},

				password: {
					label: this.newUser.email === ''
						? t('settings', 'Password (required)')
						: t('settings', 'Password'),

					required: this.newUser.email === '',
				},

				email: {
					label: this.newUser.password === '' || this.settings.newUserRequireEmail
						? t('settings', 'Email (required)')
						: t('settings', 'Email'),

					required: this.newUser.password === '' || this.settings.newUserRequireEmail,
				},

				showPasswordEmailHint: !this.settings.newUserRequireEmail,
			}
		},
	},

	mounted() {
		this.$refs.fields?.focusField('username')
	},

	methods: {
		async createUser() {
			this.loading.all = true
			try {
				await this.$store.dispatch('addUser', {
					userid: this.newUser.username,
					password: this.newUser.password,
					displayName: this.newUser.displayName,
					email: this.newUser.email,
					groups: this.newUser.groups.map(({ id }) => id),
					subadmin: this.newUser.subadminGroups.map(({ id }) => id),
					quota: this.newUser.quota.id,
					language: this.newUser.language.code,
					manager: this.newUser.manager.id,
				})

				this.$emit('closing')
			} catch (error) {
				this.loading.all = false
				if (error.response?.data?.ocs?.meta) {
					const statuscode = error.response.data.ocs.meta.statuscode
					if (statuscode === 102) {
						this.$refs.fields?.focusField('username')
					} else if (statuscode === 107) {
						this.$refs.fields?.focusField('password')
					}
				}
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.dialog {
	:deep(.dialog__actions) {
		margin-block-start: calc(var(--default-grid-baseline, 4px) * 3);
	}
}
</style>
